// ECharts cartesian heatmap. For 2D density questions like
// "swap volume by hour of day × day of week" or "alert rate by zone ×
// severity". Inputs are three column names: discrete category for the
// x-axis, discrete category for the y-axis, and a numeric column for
// the cell color.
//
// NOT for geographic visualisations — use `geo_scatter` for lat/lon
// points on a map. ECharts heatmap series has no geo coordinate system;
// pairing it with `geo: {map: "..."}` does not work.

import type { ChartRow } from "../types";
import { axisStyle, columnIndex } from "./_shared";

interface Input {
  x: string;
  y: string;
  value: string;
}

export function buildHeatmap(
  input: Input,
  rows: ChartRow[],
  columns: string[],
  _title: string,
  _palette: ReadonlyArray<string>,
) {
  const xIdx = columnIndex(columns, input.x);
  const yIdx = columnIndex(columns, input.y);
  const vIdx = columnIndex(columns, input.value);

  // Distinct x/y values, in first-seen order so the axes reflect the
  // ordering the SQL produced (e.g. Mon..Sun if the model ORDER BY'd
  // a day_of_week column). ECharts heatmap series wants [xIndex, yIndex,
  // value] triples; pre-build the index maps.
  const xs: string[] = [];
  const ys: string[] = [];
  const xMap = new Map<string, number>();
  const yMap = new Map<string, number>();
  const data: Array<[number, number, number]> = [];
  let vMin = Infinity;
  let vMax = -Infinity;
  for (const row of rows) {
    const xs_raw = row[xIdx];
    const ys_raw = row[yIdx];
    const xKey = xs_raw == null ? "—" : String(xs_raw);
    const yKey = ys_raw == null ? "—" : String(ys_raw);
    let xi = xMap.get(xKey);
    if (xi === undefined) {
      xi = xs.length;
      xs.push(xKey);
      xMap.set(xKey, xi);
    }
    let yi = yMap.get(yKey);
    if (yi === undefined) {
      yi = ys.length;
      ys.push(yKey);
      yMap.set(yKey, yi);
    }
    const v = Number(row[vIdx] ?? 0);
    if (Number.isFinite(v)) {
      if (v < vMin) vMin = v;
      if (v > vMax) vMax = v;
    }
    data.push([xi, yi, v]);
  }
  if (!Number.isFinite(vMin)) vMin = 0;
  if (!Number.isFinite(vMax)) vMax = 1;

  const FONT =
    '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';

  return {
    textStyle: { fontFamily: FONT },
    grid: { top: 32, left: 80, right: 24, bottom: 64, containLabel: true },
    tooltip: {
      position: "top",
      textStyle: { fontFamily: FONT, fontSize: 12, color: "#171717" },
      borderColor: "#e5e7eb",
      backgroundColor: "rgba(255,255,255,0.96)",
      extraCssText: "box-shadow: 0 6px 24px rgba(15,23,42,0.08); border-radius: 10px;",
      formatter: (params: unknown) => {
        const p = params as { value: [number, number, number] };
        const [xi, yi, v] = p.value;
        return `<div style="font-weight:600;color:#0f172a">${xs[xi]} × ${ys[yi]}</div><div style="margin-top:4px;color:#404040">${v.toLocaleString()}</div>`;
      },
    },
    xAxis: { ...axisStyle, type: "category" as const, data: xs, splitArea: { show: true } },
    yAxis: { ...axisStyle, type: "category" as const, data: ys, splitArea: { show: true } },
    // Continuous visualMap from min→max gives the density gradient the
    // user expects. Range colour chosen to read on white card backgrounds.
    visualMap: {
      min: vMin,
      max: vMax,
      calculable: true,
      orient: "horizontal" as const,
      left: "center",
      bottom: 8,
      itemHeight: 80,
      textStyle: { fontFamily: FONT, fontSize: 11, color: "#525252" },
      inRange: {
        color: ["#eef2ff", "#c7d2fe", "#818cf8", "#4f46e5", "#312e81"],
      },
    },
    series: [
      {
        type: "heatmap" as const,
        data,
        label: { show: false },
        emphasis: { itemStyle: { shadowBlur: 8, shadowColor: "rgba(0,0,0,0.18)" } },
      },
    ],
  };
}
